import React, { useState } from 'react';
import { AlertTriangle, Settings, X, Smartphone } from 'lucide-react';
import { Alert, AlertDescription } from './alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/contexts/I18nContext';
import { IOSMicrophoneGuide } from './IOSMicrophoneGuide';

interface MicrophonePermissionAlertProps {
  isVisible: boolean;
  onDismiss?: () => void;
  onRetry?: () => void;
  className?: string;
}

export const MicrophonePermissionAlert: React.FC<MicrophonePermissionAlertProps> = ({
  isVisible,
  onDismiss,
  onRetry,
  className
}) => {
  const { t } = useTranslation();
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  if (!isVisible) return null;

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           // @ts-ignore - platform is deprecated but still needed for iOS detection
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  const handleOpenSettings = () => {
    // 提供一些通用的指导信息
    const userAgent = navigator.userAgent;
    let instructions = '';

    if (userAgent.includes('Chrome')) {
      instructions = t('ui.chromeInstructions');
    } else if (userAgent.includes('Firefox')) {
      instructions = t('ui.firefoxInstructions');
    } else if (userAgent.includes('Safari')) {
      instructions = t('ui.safariInstructions');
    } else {
      instructions = t('ui.genericInstructions');
    }

    alert(`${t('ui.microphonePermissionSteps')}\n\n${instructions}\n\n${t('ui.settingsCompleteRefresh')}`);
  };

  // Show iOS-specific guide if on iOS and user wants detailed instructions
  if (showIOSGuide && isIOS()) {
    return (
      <div className={cn(
        "fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-lg mx-4",
        className
      )}>
        <IOSMicrophoneGuide
          onRetry={onRetry}
          onDismiss={() => {
            setShowIOSGuide(false);
            onDismiss?.();
          }}
        />
      </div>
    );
  }

  return (
    <div className={cn(
      "fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md mx-4",
      className
    )}>
      <Alert variant="destructive" className="shadow-lg border-2 animate-in slide-in-from-top-2 duration-300">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <div className="flex-1 pr-2">
            <div className="font-medium mb-1">{t('ui.microphoneAccessDenied')}</div>
            <div className="text-sm">
              {t('ui.microphoneInstructions')}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isIOS() ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowIOSGuide(true)}
                className="text-xs h-7 bg-background hover:bg-muted"
              >
                <Smartphone className="w-3 h-3 mr-1" />
                {t('ui.iosHelp')}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenSettings}
                className="text-xs h-7 bg-background hover:bg-muted"
              >
                <Settings className="w-3 h-3 mr-1" />
                {t('ui.openSettings')}
              </Button>
            )}
            {onDismiss && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="h-7 w-7 p-0 hover:bg-muted"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};